import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import AgentLayout from "../components/layout/AgentLayout";
import PageHeader from "../components/common/PageHeader";
import { DASHBOARD_CRUMB } from "../components/common/Breadcrumb";
import AgentSelect from "../components/common/AgentSelect";
import AddCustomerModal from "../components/customers/AddCustomerModal";
import PageLoader from "../components/common/PageLoader";
import StatusBadge from "../components/common/StatusBadge";
import { agentApi } from "../lib/apiClient";
import { showApiError, showErrorAction, showSuccessAction } from "../lib/alerts";
import { formatCurrency, packageBookingTotal, packageNetPrice, packageRsp } from "../lib/catalog";
import { packageSeatSummary } from "../lib/packageSeats";
import { fetchCurrentAgent } from "../lib/agentAuth";
import { canWriteBookings, roleFromProfile } from "../lib/agencyAccess";
import {
  customerIdOf,
  fetchAgentCustomers,
  matchCreatedCustomer,
  parseCustomers,
  toCustomerSelectOptions,
  upsertCustomer,
} from "../lib/customers";
import {
  getDataShape,
  hotelHasContent,
  normalizeFlightLeg,
  normalizeFlightSummary,
  normalizeHotel,
  normalizeItineraryItem,
} from "../components/packages/packageDetailModel";

function formatDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
}

async function fetchPackageBySlugOrId(identifier) {
  try {
    return await agentApi.get(`/umrah-packages/slug/${encodeURIComponent(identifier)}`);
  } catch (error) {
    if (error.status === 404 || error.status === 400) {
      return agentApi.get(`/umrah-packages/id/${encodeURIComponent(identifier)}`);
    }
    throw error;
  }
}

function InheritedList({ title, empty, items }) {
  return (
    <div className="mb-3">
      <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
        <h6 className="mb-0">{title}</h6>
        <StatusBadge value="assigned">From package</StatusBadge>
      </div>
      {items.length ? (
        <ul className="list-unstyled mb-0 small text-secondary">
          {items.map((item) => (
            <li key={item.key} className="mb-1">{item.label}</li>
          ))}
        </ul>
      ) : (
        <p className="small text-secondary mb-0">{empty}</p>
      )}
    </div>
  );
}

export default function PackageBookingPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pkg, setPkg] = useState(null);
  const [related, setRelated] = useState({ hotels: [], itinerary: [], flightLegs: [], flightSummary: null });
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState({
    customerId: "",
    upbu_booking_date: formatDate(new Date()),
    upbu_no_of_passengers: 1,
    upbu_booking_cust_type: "Customer",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState("");
  const [addCustomerOpen, setAddCustomerOpen] = useState(false);
  const [customerMenuOpen, setCustomerMenuOpen] = useState(false);
  const [canCreate, setCanCreate] = useState(false);

  const seats = packageSeatSummary(pkg || {});
  const soldOut = seats.soldOut;
  const maxPassengers = Math.max(seats.available || 0, 0);
  const requestedPassengers = parseInt(form.upbu_no_of_passengers, 10) || 0;
  const exceedsAvailability = !soldOut && maxPassengers > 0 && requestedPassengers > maxPassengers;
  const canSubmit = canCreate && !soldOut && requestedPassengers >= 1 && !exceedsAvailability;

  const customerOptions = useMemo(() => {
    const options = toCustomerSelectOptions(customers);
    if (options.length === 0) return [];
    return [{ label: "Customers", options }];
  }, [customers]);

  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        const profile = await fetchCurrentAgent().catch(() => null);
        if (!alive) return;
        const allowed = canWriteBookings(roleFromProfile(profile));
        setCanCreate(allowed);
        if (!allowed) {
          navigate(`/packages/${id}`, { replace: true });
          return;
        }
        const [packagePayload, customerPayload] = await Promise.all([
          fetchPackageBySlugOrId(id),
          agentApi.get("/travel-partner/customers?limit=100&page=1"),
        ]);
        if (!alive) return;
        const shaped = getDataShape(packagePayload);
        const nextPkg = shaped.package || {};
        const hotels = (shaped.hotels || []).map(normalizeHotel).filter(hotelHasContent);
        const itinerary = (shaped.itinerary || []).map(normalizeItineraryItem);
        const flightLegs = (shaped.flightBlockDetails || []).map(normalizeFlightLeg);
        setPkg(nextPkg);
        setRelated({
          hotels,
          itinerary,
          flightLegs,
          flightSummary: normalizeFlightSummary(nextPkg, shaped.flightBlock, flightLegs),
        });
        setCustomers(parseCustomers(customerPayload));
        const availability = packageSeatSummary(nextPkg);
        if (availability.available > 0) {
          setForm((current) => ({
            ...current,
            upbu_no_of_passengers: Math.min(parseInt(current.upbu_no_of_passengers, 10) || 1, availability.available),
          }));
        }
      } catch (fetchError) {
        if (alive) showApiError(fetchError, "LOAD_BOOKING_FORM");
      } finally {
        if (alive) setLoading(false);
      }
    }
    load();
    return () => {
      alive = false;
    };
  }, [id, navigate]);

  function openAddCustomer() {
    setCustomerMenuOpen(false);
    setAddCustomerOpen(true);
  }

  function handleCustomerCreated(customer) {
    const createdId = customerIdOf(customer);
    setCustomers((current) => upsertCustomer(current, customer));
    if (createdId) {
      setForm((current) => ({ ...current, customerId: createdId }));
    }
    setAddCustomerOpen(false);
    setCustomerMenuOpen(false);
    showSuccessAction("CREATE_CUSTOMER");

    fetchAgentCustomers({ limit: 100, page: 1 })
      .then((list) => {
        const resolved = list.find((item) => customerIdOf(item) === createdId)
          || matchCreatedCustomer(list, customer)
          || customer;
        setCustomers(upsertCustomer(list, resolved));
        const resolvedId = customerIdOf(resolved);
        if (resolvedId) {
          setForm((current) => ({ ...current, customerId: resolvedId }));
        }
      })
      .catch(() => {
        if (!createdId) {
          showErrorAction("CUSTOMER_LIST_REFRESH");
        }
      });
  }

  function updatePassengerCount(value) {
    const next = value === "" ? "" : parseInt(value, 10);
    setForm((current) => ({
      ...current,
      upbu_no_of_passengers: Number.isFinite(next) ? next : value,
    }));
  }

  async function createBooking(status) {
    if (!canCreate) {
      showErrorAction("CREATE_BOOKING");
      return;
    }
    if (soldOut) {
      showErrorAction("PACKAGE_SOLD_OUT");
      return;
    }
    if (exceedsAvailability || requestedPassengers < 1) {
      showErrorAction("EXCEED_PACKAGE_SEATS");
      return;
    }
    try {
      setSaving(status);
      const packageId = pkg?.id || pkg?.u_pkg_id;
      if (!packageId) {
        throw new Error("Package id is missing, so the booking cannot be created.");
      }
      const payload = await agentApi.post(`/umrah-packages/${packageId}/bookings`, {
        body: {
          ...form,
          customerId: form.customerId ? parseInt(form.customerId, 10) : null,
          upbu_no_of_passengers: requestedPassengers,
          upbu_booking_status: status,
          upbu_total: packageBookingTotal(pkg, requestedPassengers),
          upbu_rate_twin_share: packageNetPrice(pkg),
        },
      });
      const booking = payload?.data?.booking || payload?.booking || payload?.data || payload;
      const bookingId = booking?.upbu_id || booking?.booking_id || booking?.id || "";
      const noticeAction = status === "draft" ? "SAVE_DRAFT" : "SUBMIT_BOOKING";
      navigate(`/bookings/${bookingId}`, { state: { noticeAction, notice: noticeAction } });
    } catch (saveError) {
      showApiError(saveError, status === "draft" ? "SAVE_DRAFT" : "CREATE_BOOKING");
    } finally {
      setSaving("");
    }
  }

  const hotelItems = related.hotels.map((hotel, index) => ({
    key: hotel.id || `${hotel.name}-${index}`,
    label: [hotel.city, hotel.name, hotel.nights ? `${hotel.nights} nights` : ""]
      .filter(Boolean)
      .join(" · ") || "Hotel included",
  }));
  const flightItems = related.flightLegs.map((leg, index) => ({
    key: leg.id || `${leg.code}-${index}`,
    label: [leg.leg === "return" ? "Return" : "Onward", leg.airline, leg.code, leg.departure && leg.arrival ? `${leg.departure} → ${leg.arrival}` : ""]
      .filter(Boolean)
      .join(" · "),
  }));
  const transportLabel = [pkg?.transportType, pkg?.transportName, pkg?.transportVehicle, pkg?.transportCity]
    .filter(Boolean)
    .join(" · ");
  const itineraryItems = related.itinerary.slice(0, 6).map((item, index) => ({
    key: item.id || `day-${index}`,
    label: [item.day ? `Day ${item.day}` : item.title, item.city, item.title || item.description]
      .filter(Boolean)
      .join(" · "),
  }));
  const roomItems = related.hotels
    .filter((hotel) => hotel.nights)
    .map((hotel, index) => ({
      key: `room-${hotel.id || index}`,
      label: `${hotel.city || "Stay"} · ${hotel.nights} night${Number(hotel.nights) === 1 ? "" : "s"}`,
    }));

  return (
    <AgentLayout>
      <div className="container-fluid py-24">
        <PageHeader
          eyebrow="Create booking"
          title={pkg?.name || "Package booking"}
          items={[
            DASHBOARD_CRUMB,
            { label: "Services" },
            { label: "Packages", href: "/packages" },
            { label: pkg?.name || "Package Details", href: `/packages/${id}` },
            { label: "Book Package" },
          ]}
          backTo={`/packages/${id}`}
        />

        {loading || !canCreate ? <PageLoader message="Loading booking form..." /> : null}

        {!loading && canCreate ? (
          <div className="row g-4">
            <div className="col-12 col-lg-4">
              <div className="card border-0 shadow-sm">
                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
                    <h5 className="mb-0">Booking details</h5>
                    {soldOut ? <StatusBadge value="Sold Out" /> : null}
                  </div>
                  {soldOut ? (
                    <div className="alert alert-warning" role="alert">
                      This package is sold out. No remaining seats are available.
                    </div>
                  ) : (
                    <div className="alert alert-info py-2 px-3" role="status">
                      Available seats {seats.available} / {seats.total || seats.available}
                    </div>
                  )}
                  <div className="mb-3">
                    <label className="form-label">Customer</label>
                    <AgentSelect
                      value={form.customerId}
                      options={customerOptions}
                      onChange={(value) => setForm({ ...form, customerId: value })}
                      placeholder={customerMenuOpen ? "Search customers..." : "Select customer"}
                      isClearable
                      menuIsOpen={customerMenuOpen}
                      onMenuOpen={() => setCustomerMenuOpen(true)}
                      onMenuClose={() => setCustomerMenuOpen(false)}
                      noOptionsMessage={() => "No customers found"}
                      isDisabled={soldOut}
                      menuAction={{
                        label: "+ Add Customer",
                        onClick: openAddCustomer,
                      }}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Departure date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={form.upbu_booking_date}
                      disabled={soldOut}
                      onChange={(event) => setForm({ ...form, upbu_booking_date: event.target.value })}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Pilgrim count</label>
                    <input
                      type="number"
                      min="1"
                      max={maxPassengers || undefined}
                      className="form-control"
                      value={form.upbu_no_of_passengers}
                      disabled={soldOut}
                      onChange={(event) => updatePassengerCount(event.target.value)}
                    />
                    {exceedsAvailability ? (
                      <div className="text-danger small mt-1">
                        Only {maxPassengers} seat{maxPassengers === 1 ? "" : "s"} remaining.
                      </div>
                    ) : null}
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      disabled={Boolean(saving) || !canSubmit}
                      onClick={() => createBooking("draft")}
                    >
                      {saving === "draft" ? "Saving..." : "Save draft"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={Boolean(saving) || !canSubmit}
                      onClick={() => createBooking("submitted")}
                    >
                      {saving === "submitted" ? "Submitting..." : (soldOut ? "Sold Out" : "Submit request")}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-lg-8">
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-body">
                  <h5 className="mb-3">Package snapshot</h5>
                  <div className="row g-3 small text-secondary">
                    <div className="col-6"><strong>Name:</strong> {pkg?.name || "Not set"}</div>
                    <div className="col-6"><strong>Code:</strong> {pkg?.code || "Not set"}</div>
                    <div className="col-6"><strong>Departure:</strong> {pkg?.formattedDepartureDate || pkg?.departureDate || "Not set"}</div>
                    <div className="col-6"><strong>Available seats:</strong> {seats.total ? `${seats.available} / ${seats.total}` : "Not set"}</div>
                    <div className="col-6"><strong>Net:</strong> {formatCurrency(packageNetPrice(pkg))}</div>
                    <div className="col-6"><strong>RSP:</strong> {formatCurrency(packageRsp(pkg))}</div>
                    <div className="col-6"><strong>Booking total:</strong> {formatCurrency(packageBookingTotal(pkg, requestedPassengers || 1))}</div>
                    <div className="col-12"><strong>Status:</strong> {soldOut ? "Sold Out" : (pkg?.status || "Unknown")}</div>
                  </div>
                </div>
              </div>

              <div className="card border-0 shadow-sm">
                <div className="card-body">
                  <div className="d-flex align-items-start justify-content-between gap-2 mb-3">
                    <div>
                      <h5 className="mb-1">Included package services</h5>
                      <p className="small text-secondary mb-0">
                        Hotel, flight, transport, rooms and itinerary are inherited from this package and cannot be reassigned here.
                      </p>
                    </div>
                    <Link to={`/packages/${id}`} className="btn btn-outline-secondary btn-sm">View package</Link>
                  </div>
                  <InheritedList
                    title="Hotels"
                    empty="No hotels are configured on this package."
                    items={hotelItems}
                  />
                  <InheritedList
                    title="Flights"
                    empty={related.flightSummary?.name ? related.flightSummary.name : "No flights are configured on this package."}
                    items={flightItems}
                  />
                  <InheritedList
                    title="Transport"
                    empty="No transport is configured on this package."
                    items={transportLabel ? [{ key: "transport", label: transportLabel }] : []}
                  />
                  <InheritedList
                    title="Rooms"
                    empty="Room stay details come from the package hotels."
                    items={roomItems}
                  />
                  <InheritedList
                    title="Itinerary"
                    empty="No itinerary is configured on this package."
                    items={itineraryItems}
                  />
                </div>
              </div>
            </div>
          </div>
        ) : null}

        <AddCustomerModal
          open={addCustomerOpen}
          onClose={() => setAddCustomerOpen(false)}
          onCreated={handleCustomerCreated}
        />
      </div>
    </AgentLayout>
  );
}
